import React, { useState, useEffect, useRef, useContext } from 'react';
import { Dropdown, Spinner } from 'react-bootstrap';
import AppContext from 'context/Context';
import useToken from 'hooks/useToken';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faSearch,
  faTimes,
  faCheck,
  faBuilding,
  faChevronDown
} from '@fortawesome/free-solid-svg-icons';

const PAGE_SIZE = 20;

const ClientFilterSelect = ({
  selectedClientId,
  onSelectClient,
  variant = 'default',
  className = ''
}) => {
  const { config: { isDark: contextIsDark } = {} } = useContext(AppContext);
  const isDark = typeof contextIsDark === 'boolean'
    ? contextIsDark
    : (typeof document !== 'undefined' && (document.documentElement.classList.contains('dark') || document.body.classList.contains('dark')));

  const { token } = useToken();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [clients, setClients] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedClientName, setSelectedClientName] = useState('');

  const scrollContainerRef = useRef(null);
  const lastSearchRef = useRef(null);

  // Debounce del input de búsqueda (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Consulta paginada a la base de datos
  const fetchClients = async (search, targetPage, append = false) => {
    if (!token) return;
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    try {
      const url = `/api/SupportEngine/GetClientsList?searchTerm=${encodeURIComponent(search || '')}&pageNumber=${targetPage}&pageSize=${PAGE_SIZE}`;
      const response = await fetch(url, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        }
      });

      if (response.ok) {
        const result = await response.json();
        const items = result.data || (Array.isArray(result) ? result : []);
        const list = Array.isArray(items) ? items : [];

        if (append) {
          setClients(prev => [...prev, ...list]);
        } else {
          setClients(list);
        }

        setHasMore(list.length >= PAGE_SIZE);
      }
    } catch (err) {
      console.error('Error al cargar clientes paginados:', err);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Carga inicial al abrir o cuando cambia el término de búsqueda debounced
  useEffect(() => {
    if (!isOpen) return;

    if (lastSearchRef.current !== debouncedSearch || clients.length === 0) {
      lastSearchRef.current = debouncedSearch;
      setPage(1);
      fetchClients(debouncedSearch, 1, false);
    }
  }, [debouncedSearch, isOpen]);

  // Manejador del scroll infinito en el contenedor de resultados
  const handleScroll = e => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      if (!isLoading && !isLoadingMore && hasMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchClients(debouncedSearch, nextPage, true);
      }
    }
  };

  const isAll = !selectedClientId || selectedClientId === '' || selectedClientId === 'todos';

  const handleSelectClient = client => {
    if (!client) {
      setSelectedClientName('');
      onSelectClient('');
    } else {
      setSelectedClientName(client.vNombreCompleto);
      onSelectClient(String(client.idClienteAkasia));
    }
    setIsOpen(false);
  };

  const handleClear = e => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedClientName('');
    onSelectClient('');
  };

  const displayName = isAll ? 'Todos los clientes' : selectedClientName;

  const renderDropdownMenu = () => (
    <Dropdown.Menu
      className={`shadow-lg border p-2 ${variant === 'chip' ? 'w-100' : ''}`}
      style={{
        minWidth: '260px',
        maxWidth: variant === 'chip' ? '100%' : '320px',
        width: variant === 'chip' ? '100%' : undefined,
        backgroundColor: isDark ? '#0f172a' : '#ffffff',
        borderColor: isDark ? '#334155' : '#e2e8f0',
        zIndex: 1050
      }}
    >
      {/* Buscador interno debounced */}
      <div className="position-relative mb-2 px-1">
        <input
          type="text"
          className="form-control form-control-sm ps-4 pe-4 shadow-none"
          placeholder="Buscar por nombre o ID..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          autoFocus
          style={{
            fontSize: '0.78rem',
            backgroundColor: isDark ? '#1e293b' : '#f8fafc',
            color: isDark ? '#f1f5f9' : '#0f172a',
            borderColor: isDark ? '#334155' : '#e2e8f0'
          }}
        />
        <FontAwesomeIcon
          icon={faSearch}
          className="position-absolute text-muted"
          style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.72rem' }}
        />
        {searchTerm && (
          <button
            type="button"
            className="btn btn-link btn-sm position-absolute p-0 text-muted"
            style={{ right: '12px', top: '50%', transform: 'translateY(-50%)', textDecoration: 'none' }}
            onClick={() => setSearchTerm('')}
          >
            <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.72rem' }} />
          </button>
        )}
      </div>

      {/* Opción 1 fija: Todos los clientes */}
      <Dropdown.Item
        as="button"
        type="button"
        className="d-flex align-items-center justify-content-between py-1 px-2 rounded-2"
        active={isAll}
        onClick={() => handleSelectClient(null)}
        style={{
          fontSize: '0.80rem',
          color: isAll ? '#ffffff' : (isDark ? '#e2e8f0' : '#1e293b'),
          backgroundColor: isAll ? '#0284c7' : 'transparent'
        }}
      >
        <div className="d-flex align-items-center text-truncate">
          <span>Todos los clientes</span>
        </div>
      </Dropdown.Item>

      {/* Lista scrollable con paginación infinita */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="overflow-y-auto pe-1"
        style={{ maxHeight: '240px', overflowY: 'auto' }}
      >
        {isLoading && clients.length === 0 ? (
          <div className="text-center py-3 text-muted" style={{ fontSize: '0.78rem' }}>
            <Spinner animation="border" size="sm" className="me-2 text-primary" />
            Cargando clientes...
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-3 text-muted" style={{ fontSize: '0.78rem' }}>
            No se encontraron clientes
          </div>
        ) : (
          <>
            {clients.map(client => {
              const isSelected = String(client.idClienteAkasia) === String(selectedClientId);

              return (
                <Dropdown.Item
                  key={client.idClienteAkasia}
                  as="button"
                  type="button"
                  className="d-flex align-items-center justify-content-between py-1 px-2 rounded-2 text-truncate"
                  active={isSelected}
                  onClick={() => handleSelectClient(client)}
                  style={{
                    fontSize: '0.80rem',
                    color: isSelected ? '#ffffff' : (isDark ? '#e2e8f0' : '#1e293b'),
                    backgroundColor: isSelected ? '#0284c7' : 'transparent'
                  }}
                >
                  <div className="d-flex align-items-center gap-2 text-truncate">
                    <FontAwesomeIcon icon={faUser} style={{ width: 14, opacity: 0.6 }} />
                    <span className="text-truncate">{client.vNombreCompleto}</span>
                  </div>
                  {isSelected && <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.75rem' }} />}
                </Dropdown.Item>
              );
            })}

            {isLoadingMore && (
              <div className="text-center py-2 text-muted" style={{ fontSize: '0.72rem' }}>
                <Spinner animation="border" size="sm" className="me-1 text-primary" />
                Cargando más clientes...
              </div>
            )}
          </>
        )}
      </div>
    </Dropdown.Menu>
  );

  if (variant === 'chip') {
    const isSelected = !isAll;

    return (
      <Dropdown
        show={isOpen}
        onToggle={open => setIsOpen(open)}
        className={`flex-shrink-0 ${className ? className : 'd-inline-block'}`}
      >
        <Dropdown.Toggle
          as="div"
          role="button"
          tabIndex={0}
          className="d-inline-flex align-items-center gap-1.5 px-3 py-2 rounded-pill shadow-none dropdown-caret-none w-100 justify-content-between"
          style={{
            cursor: 'pointer',
            minWidth: 0,
            backgroundColor: isSelected
              ? (isDark ? 'rgba(99, 102, 241, 0.15)' : '#e0e7ff')
              : (isDark ? '#1e293b' : '#ffffff'),
            color: isSelected
              ? (isDark ? '#818cf8' : '#4f46e5')
              : (isDark ? '#cbd5e1' : '#334155'),
            border: isSelected
              ? (isDark ? '1px solid rgba(99, 102, 241, 0.35)' : '1px solid #c7d2fe')
              : (isDark ? '1px solid #334155' : '1px solid #e2e8f0'),
            fontSize: '0.82rem',
            fontWeight: 500,
            whiteSpace: 'nowrap'
          }}
          title={displayName}
        >
          <div className="d-flex align-items-center gap-1.5 overflow-hidden flex-grow-1 min-w-0 me-1" style={{ minWidth: 0 }}>
            <span className="text-truncate">
              {displayName}
            </span>
          </div>
          <div className="d-flex align-items-center flex-shrink-0 ms-auto">
            {isSelected && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClear}
                className="me-1 d-inline-flex align-items-center justify-content-center"
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  color: isDark ? '#818cf8' : '#4f46e5',
                  cursor: 'pointer'
                }}
                title="Quitar filtro de cliente"
              >
                <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.68rem' }} />
              </span>
            )}
            <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '0.68rem', opacity: 0.7 }} />
          </div>
        </Dropdown.Toggle>

        {renderDropdownMenu()}
      </Dropdown>
    );
  }

  return (
    <div className={`d-flex align-items-center gap-2 se-client-filter-container ${className}`}>
      <label
        htmlFor="tickets-client-filter-btn"
        className="d-flex align-items-center gap-1 text-600 mb-0 fw-semibold"
        style={{ fontSize: '0.80rem', whiteSpace: 'nowrap' }}
      >
        <FontAwesomeIcon
          icon={!isAll ? faUser : faBuilding}
          style={{ color: !isAll ? (isDark ? '#38bdf8' : '#0284c7') : (isDark ? '#94a3b8' : '#64748b') }}
        />
        <span>Cliente:</span>
      </label>

      <Dropdown show={isOpen} onToggle={open => setIsOpen(open)} className="position-relative">
        <Dropdown.Toggle
          id="tickets-client-filter-btn"
          as="button"
          type="button"
          className="form-control form-control-sm border shadow-none text-900 fw-semibold d-flex align-items-center justify-content-between text-truncate dropdown-caret-none"
          style={{
            fontSize: '0.82rem',
            borderRadius: '8px',
            minWidth: '160px',
            maxWidth: '240px',
            height: '31px',
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
            borderColor: isDark ? '#334155' : '#cbd5e1',
            color: isDark ? '#f1f5f9' : '#1e293b',
            cursor: 'pointer'
          }}
          title={displayName}
        >
          <span className="text-truncate me-auto">{displayName}</span>
          <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-2">
            {!isAll && (
              <span
                role="button"
                tabIndex={0}
                className="text-muted text-hover-danger d-inline-flex align-items-center justify-content-center"
                title="Quitar filtro de cliente"
                onClick={handleClear}
                style={{
                  width: '16px',
                  height: '16px',
                  cursor: 'pointer'
                }}
              >
                <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.68rem' }} />
              </span>
            )}
            <FontAwesomeIcon
              icon={faChevronDown}
              className="text-muted"
              style={{ fontSize: '0.62rem' }}
            />
          </div>
        </Dropdown.Toggle>

        {renderDropdownMenu()}
      </Dropdown>
    </div>
  );
};

export default ClientFilterSelect;
